import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiPlus, 
  FiEdit3, 
  FiTrash2, 
  FiUsers, 
  FiEye, 
  FiSearch,
  FiFilter,
  FiRefreshCw
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { cargoService } from '../../services/api';
import './Cargos.css';

const Cargos = () => {
  const navigate = useNavigate();
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState('todos');
  const [cargoParaExcluir, setCargoParaExcluir] = useState(null);
  const [mostrarModalExclusao, setMostrarModalExclusao] = useState(false);

  useEffect(() => {
    carregarCargos();
  }, []);

  const carregarCargos = async () => {
    try {
      setLoading(true);
      const response = await cargoService.getAll();
      if (response.success) {
        setCargos(response.data || []);
      } else {
        toast.error('Erro ao carregar cargos');
      }
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
      toast.error('Erro ao carregar cargos');
    } finally {
      setLoading(false);
    }
  };

  const confirmarExclusao = (cargo) => {
    setCargoParaExcluir(cargo);
    setMostrarModalExclusao(true);
  };

  const excluirCargo = async () => {
    if (!cargoParaExcluir) return;

    try {
      const response = await cargoService.delete(cargoParaExcluir.id);
      if (response.success) {
        toast.success('Cargo excluído com sucesso!');
        carregarCargos();
      } else {
        toast.error(response.message || 'Erro ao excluir cargo');
      }
    } catch (error) {
      console.error('Erro ao excluir cargo:', error);
      toast.error('Erro ao excluir cargo');
    } finally {
      setMostrarModalExclusao(false);
      setCargoParaExcluir(null);
    }
  };

  const toggleStatus = async (cargo) => {
    try {
      const dadosAtualizados = {
        nome: cargo.nome,
        descricao: cargo.descricao,
        ativo: !cargo.ativo
      };

      const response = await cargoService.update(cargo.id, dadosAtualizados);
      if (response.success) {
        toast.success(`Cargo ${cargo.ativo ? 'desativado' : 'ativado'} com sucesso!`);
        carregarCargos();
      } else {
        toast.error(response.message || 'Erro ao alterar status do cargo');
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status do cargo');
    }
  };

  const cargosFiltrados = cargos.filter(cargo => {
    const matchSearch = cargo.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (cargo.descricao && cargo.descricao.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchFilter = filtroAtivo === 'todos' || 
                       (filtroAtivo === 'ativo' && cargo.ativo) ||
                       (filtroAtivo === 'inativo' && !cargo.ativo);
    
    return matchSearch && matchFilter;
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <FiRefreshCw className="spin" />
          <p>Carregando cargos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cargos-page">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <FiUsers className="page-icon" />
            Gerenciar Cargos
          </h1>
          <p>Gerencie os cargos disponíveis na congregação</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => navigate('/cargos/cadastro')}
        >
          <FiPlus /> Novo Cargo
        </button>
      </div>

      <div className="page-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por nome ou descrição..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <FiFilter className="filter-icon" />
          <select 
            value={filtroAtivo} 
            onChange={(e) => setFiltroAtivo(e.target.value)}
          >
            <option value="todos">Todos</option>
            <option value="ativo">Ativos</option>
            <option value="inativo">Inativos</option>
          </select>
        </div>

        <button
          className="btn-refresh"
          onClick={carregarCargos}
          title="Atualizar lista"
        >
          <FiRefreshCw />
        </button>
      </div>

      <div className="cargos-stats">
        <div className="stat-card">
          <span className="stat-number">{cargos.length}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-card active">
          <span className="stat-number">{cargos.filter(c => c.ativo).length}</span>
          <span className="stat-label">Ativos</span>
        </div>
        <div className="stat-card inactive">
          <span className="stat-number">{cargos.filter(c => !c.ativo).length}</span>
          <span className="stat-label">Inativos</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{cargosFiltrados.length}</span>
          <span className="stat-label">Filtrados</span>
        </div>
      </div>

      {cargosFiltrados.length === 0 ? (
        <div className="empty-state">
          <FiUsers className="empty-icon" />
          <h3>Nenhum cargo encontrado</h3>
          <p>
            {searchTerm || filtroAtivo !== 'todos' 
              ? 'Tente ajustar os filtros de busca'
              : 'Comece criando o primeiro cargo'
            }
          </p>
          {!searchTerm && filtroAtivo === 'todos' && (
            <button
              className="btn-primary"
              onClick={() => navigate('/cargos/cadastro')}
            >
              <FiPlus /> Criar Primeiro Cargo
            </button>
          )}
        </div>
      ) : (
        <div className="cargos-grid">
          {cargosFiltrados.map(cargo => (
            <div
              key={cargo.id}
              role="button"
              tabIndex={0}
              className={`cargo-card ${!cargo.ativo ? 'inactive' : ''}`}
              onClick={() => navigate(`/cargos/${cargo.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/cargos/${cargo.id}`);
                }
              }}
            >
              <div className="cargo-header">
                <h3>{cargo.nome}</h3>
                <div className="cargo-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="btn-icon"
                    onClick={() => navigate(`/cargos/${cargo.id}`)}
                    title="Visualizar"
                  >
                    <FiEye />
                  </button>
                  <button
                    className="btn-icon edit"
                    onClick={() => navigate(`/cargos/editar/${cargo.id}`)}
                    title="Editar"
                  >
                    <FiEdit3 />
                  </button>
                  <button
                    className="btn-icon delete"
                    onClick={() => confirmarExclusao(cargo)}
                    title="Excluir"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
              
              <div className="cargo-body">
                {cargo.descricao && (
                  <p className="cargo-descricao">{cargo.descricao}</p>
                )}
                
                <div className="cargo-info">
                  <span className={`status-badge ${cargo.ativo ? 'active' : 'inactive'}`}>
                    {cargo.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                  
                  {cargo.total_colaboradores !== undefined && (
                    <span className="colaboradores-count">
                      <FiUsers />
                      {cargo.total_colaboradores} colaborador(es)
                    </span>
                  )}
                </div>
              </div>

              <div className="cargo-footer" onClick={(e) => e.stopPropagation()}>
                <button
                  className={`btn-toggle ${cargo.ativo ? 'deactivate' : 'activate'}`}
                  onClick={() => toggleStatus(cargo)}
                >
                  {cargo.ativo ? 'Desativar' : 'Ativar'}
                </button>
                
                <small className="created-date">
                  Criado em:{' '}
                  {(() => {
                    const raw = cargo.created_at ?? cargo.createdAt;
                    const d = raw ? new Date(raw) : null;
                    return d && !Number.isNaN(d.getTime())
                      ? d.toLocaleDateString('pt-BR')
                      : '—';
                  })()}
                </small>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {mostrarModalExclusao && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirmar Exclusão</h3>
            </div>
            <div className="modal-body">
              <p>
                Tem certeza que deseja excluir o cargo <strong>{cargoParaExcluir?.nome}</strong>?
              </p>
              <p className="warning">
                ⚠️ Esta ação não pode ser desfeita. Colaboradores vinculados a este cargo 
                terão sua vinculação removida.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setMostrarModalExclusao(false);
                  setCargoParaExcluir(null);
                }}
              >
                Cancelar
              </button>
              <button
                className="btn-danger"
                onClick={excluirCargo}
              >
                <FiTrash2 /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cargos; 